/**
 * SimCardThumb - Compact card thumbnail with an action caption
 *
 * Used by the turn timeline to show what entered and left the hand each turn,
 * and by the "still in hand" strip.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import type { SimCard } from '@/features/decks/lib/handSim';

import SimCardArt from './SimCardArt';

// ============================================================================
// Types
// ============================================================================

export type SimCardTone = 'drawn' | 'land' | 'cast' | 'stuck';

export interface SimCardThumbProps {
  card: SimCard;
  tone: SimCardTone;
  /** Short action label rendered under the thumbnail */
  caption: string;
  onOpenDetails: (card: SimCard) => void;
}

const TONE_RING: Record<SimCardTone, string> = {
  drawn: 'ring-2 ring-sky-400/70',
  land: 'ring-2 ring-emerald-500/70',
  cast: 'ring-2 ring-violet-400/70',
  stuck: 'ring-1 ring-zinc-700',
};

const TONE_TEXT: Record<SimCardTone, string> = {
  drawn: 'text-sky-300',
  land: 'text-emerald-300',
  cast: 'text-violet-300',
  stuck: 'text-ash/70',
};

// ============================================================================
// Component
// ============================================================================

export default function SimCardThumb({ card, tone, caption, onOpenDetails }: SimCardThumbProps): ReactElement {
  return (
    <CardHoverPreview imageUris={card.imageUris} name={card.name}>
      <div className="w-11">
        <button
          type="button"
          onClick={() => onOpenDetails(card)}
          aria-label={`${card.name} — ${caption}. Open card details`}
          className={clsx(
            'block aspect-488/680 w-full overflow-hidden rounded-sm shadow transition-transform hover:scale-110',
            'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
            TONE_RING[tone],
            tone === 'stuck' && 'brightness-75',
          )}
        >
          <SimCardArt card={card} fallbackClassName="text-[7px] leading-tight" />
        </button>
        <div className={clsx('mt-0.5 truncate text-center text-[9px]', TONE_TEXT[tone])} title={caption}>
          {caption}
        </div>
      </div>
    </CardHoverPreview>
  );
}
